import React, { useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Mail, MapPin, Clock, Send, ShieldCheck } from "lucide-react";
import { submitContact } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await submitContact(form);
      toast.success("Message sent!", { description: "We'll get back to you within 24 hours." });
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page" className="mx-auto max-w-7xl px-5 py-16 lg:px-8 lg:py-24">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
        <span className="font-mono text-xs uppercase tracking-[0.2em] text-primary">Contact</span>
        <h1 className="mt-3 font-heading text-4xl font-semibold tracking-tighter text-[#FAFAFA] sm:text-5xl">
          Get in touch
        </h1>
        <p className="mt-4 max-w-xl text-base text-[#A1A1AA]">
          Questions about fitment, overclocking, or bulk orders? Drop us a line.
        </p>
      </motion.div>

      <div className="mt-12 grid gap-10 lg:grid-cols-5">
        <div className="space-y-6 lg:col-span-2">
          {[
            { icon: Mail, t: "Email", v: "support@asicmod.com" },
            { icon: Clock, t: "Response time", v: "Within 24 hours" },
            { icon: MapPin, t: "Workshop", v: "Ships worldwide" },
          ].map((c) => (
            <div key={c.t} className="flex items-start gap-4 rounded-sm border border-[#27272A] bg-[#0E0E11] p-5">
              <c.icon size={20} className="mt-0.5 text-primary" />
              <div>
                <div className="text-sm font-medium text-[#FAFAFA]">{c.t}</div>
                <div className="text-sm text-[#A1A1AA]">{c.v}</div>
              </div>
            </div>
          ))}
          <div className="flex items-center gap-2 rounded-sm border border-[#27272A] bg-[#0E0E11] p-5 text-xs text-[#A1A1AA]">
            <ShieldCheck size={16} className="text-primary" /> Your details are stored securely and never shared.
          </div>
        </div>

        <form onSubmit={onSubmit} data-testid="contact-form" className="space-y-5 rounded-sm border border-[#27272A] bg-[#0E0E11] p-8 lg:col-span-3">
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <Label htmlFor="name" className="text-[#A1A1AA]">Name</Label>
              <Input id="name" data-testid="contact-name" value={form.name} onChange={set("name")} placeholder="Satoshi N." className="mt-2 border-[#27272A] bg-[#09090B] focus-visible:ring-primary" />
            </div>
            <div>
              <Label htmlFor="email" className="text-[#A1A1AA]">Email</Label>
              <Input id="email" type="email" data-testid="contact-email" value={form.email} onChange={set("email")} placeholder="you@email.com" className="mt-2 border-[#27272A] bg-[#09090B] focus-visible:ring-primary" />
            </div>
          </div>
          <div>
            <Label htmlFor="subject" className="text-[#A1A1AA]">Subject</Label>
            <Input id="subject" data-testid="contact-subject" value={form.subject} onChange={set("subject")} placeholder="Fitment question" className="mt-2 border-[#27272A] bg-[#09090B] focus-visible:ring-primary" />
          </div>
          <div>
            <Label htmlFor="message" className="text-[#A1A1AA]">Message</Label>
            <Textarea id="message" data-testid="contact-message" value={form.message} onChange={set("message")} rows={5} placeholder="Tell us what you need…" className="mt-2 border-[#27272A] bg-[#09090B] focus-visible:ring-primary" />
          </div>
          <button
            type="submit"
            disabled={loading}
            data-testid="contact-submit"
            className="group flex w-full items-center justify-center gap-2 rounded-sm bg-primary px-7 py-3.5 text-sm font-semibold text-[#09090B] transition-all hover:bg-[#EA580C] hover:shadow-[0_0_20px_rgba(249,115,22,0.4)] disabled:opacity-60"
          >
            {loading ? "Sending…" : <>Send message <Send size={15} /></>}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Contact;
